import { Button, Dialog } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

/**
 * A confirmation, for the three destructive actions in this module.
 *
 * ONE COMPONENT WITH DIFFERENT WORDS, not three dialogs, because the three are genuinely the same
 * interaction — and because the words are the part that differs and therefore the part worth writing
 * carefully at each call site rather than burying in a shared default.
 *
 * NO BROWSER `confirm()`. 015 replaced the last of those; a native prompt cannot say which
 * transaction is being removed or what will still be true afterwards, which for a soft delete is the
 * whole question.
 */

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** What will happen, and what will still be true afterwards. */
  body: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  pending: boolean;
  error?: string | null;
}

export function ConfirmDialog({
  open,
  onClose,
  title,
  body,
  confirmLabel,
  onConfirm,
  pending,
  error,
}: ConfirmDialogProps): ReactNode {
  const t = useT();
  return (
    <Dialog open={open} onClose={onClose} title={title}>
      <div className="flex flex-col gap-4">
        <div className="text-sm">{body}</div>

        {error && <p className="text-danger text-sm">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="button" variant="primary" onClick={onConfirm} disabled={pending}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
